import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, FileText, Stethoscope, Home, MapPin, Minus, Plus } from 'lucide-react';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import { CATALOGUE, CATEGORIES } from '../../data/catalogueData';
import config from '../../config';
import { cartQty, setCartQty, cartAppointmentCount, readBookingCart } from '../../lib/bookingCart';
import { roundInr } from '../../lib/dates';

const TRUST = [
  { icon: FileText, label: 'Prescription-led process' },
  { icon: Stethoscope, label: 'Doctor review where required' },
  { icon: Home, label: 'Nurse-administered home visit' },
  { icon: MapPin, label: 'Gurgaon service area' },
];

function publicCategory(c) {
  const x = String(c || "").toLowerCase();
  if (x === "adults") return "adult";
  if (x.includes("paed") || x === "child" || x === "pediatric") return "paediatric";
  if (x.includes("elder") || x.includes("senior")) return "elderly";
  if (x.includes("travel")) return "travel";
  return x || "adult";
}

function brandRows(item) {
  return (item.brands || []).map((b, i) => {
    if (typeof b === "string") return { id: `${item.id}-b${i}`, name: b, indicative_price: item.indicative_price, pricing: item.pricing };
    return {
      id: b.id || `${item.id}-b${i}`,
      name: b.name || b.label || `Brand ${i + 1}`,
      indicative_price: b.indicative_price ?? item.indicative_price,
      pricing: b.pricing || item.pricing,
    };
  }).filter((b) => b.name);
}

function CatalogueCard({ item, categoryLabel, qtyFor, onQty }) {
  const cat = publicCategory(item.category);
  const brands = brandRows(item);
  const [brandId, setBrandId] = useState(brands[0]?.id || "");
  const selected = brands.find((b) => b.id === brandId) || brands[0];
  const payable = selected?.pricing?.total ?? selected?.indicative_price ?? item.payable_price ?? item.indicative_price ?? item.price;
  const cartId = selected ? `${item.id}::${selected.id}` : item.id;
  const qty = qtyFor(cartId);
  const payload = {
    id: cartId,
    service_id: item.id,
    brand_id: selected?.id || "",
    brand_name: selected?.name || "",
    name: selected ? `${item.name} · ${selected.name}` : item.name,
    category: cat,
    indicative_price: selected?.indicative_price ?? item.indicative_price ?? item.price ?? null,
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid={`catalogue-card-${item.id}`}
      className="bg-white rounded-2xl p-5 border border-[#673c96]/8 shadow-[0_4px_18px_rgb(0,0,0,0.03)] hover:shadow-[0_10px_28px_rgb(0,0,0,0.06)] transition-all duration-300 flex flex-col h-full"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#f37721]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          {categoryLabel}
        </span>
        {(item.ageGroup || item.age_group) && (
          <span className="text-[10px] text-[#673c96]/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {item.ageGroup || item.age_group}
          </span>
        )}
      </div>
      <h3 className="text-[15px] font-semibold text-[#311b52] mb-2 leading-snug" style={{ fontFamily: 'Poppins, sans-serif' }}>
        {item.name}
      </h3>
      {brands.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <p className="text-[10px] uppercase font-semibold text-[#673c96]/60">Brand</p>
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            className="w-full rounded-xl border border-[#673c96]/15 px-3 py-2 text-xs bg-white text-[#311b52]"
            data-testid={`catalogue-card-${item.id}-brand`}
          >
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}{b.indicative_price != null ? ` · ₹${Number(roundInr(b.pricing?.total ?? b.indicative_price) || 0).toLocaleString("en-IN")}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      {payable != null && Number(payable) >= 0 && (
        <p className="text-xs font-semibold text-[#311b52] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          ₹{Number(roundInr(payable) || 0).toLocaleString("en-IN")}
        </p>
      )}
      <p className="text-[11px] text-[#673c96]/70 italic mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        {item.note || item.description || 'Prescription or doctor review may be required.'}
      </p>
      <div className="mt-auto">
        {qty > 0 ? (
          <div className="flex items-center justify-between gap-2 bg-[#f37721]/8 border border-[#f37721]/25 rounded-full px-2 py-1.5" data-testid={`catalogue-card-${item.id}-qty`}>
            <button type="button" onClick={() => onQty(payload, qty - 1)} className="w-8 h-8 rounded-full bg-white text-[#f37721] flex items-center justify-center" aria-label="Decrease quantity">
              <Minus className="w-4 h-4" />
            </button>
            <span className="text-sm font-bold text-[#311b52] min-w-[2ch] text-center">{qty}</span>
            <button type="button" onClick={() => onQty(payload, qty + 1)} className="w-8 h-8 rounded-full bg-[#f37721] text-white flex items-center justify-center" aria-label="Increase quantity">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onQty(payload, 1)}
            data-testid={`catalogue-card-${item.id}-book`}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#f37721] rounded-full px-4 py-2.5 hover:bg-[#d9651a] transition-colors"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Book now
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function VaccinationCatalogue() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [activeTab, setActiveTab] = useState('all');
  const [query, setQuery] = useState('');
  const [catalogue, setCatalogue] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [cartTick, setCartTick] = useState(0);
  const [catalogueReady, setCatalogueReady] = useState(false);

  useEffect(() => {
    axios.get(`${config.API}/config/vaccine-categories`)
      .then(({ data }) => {
        const items = data.items || [];
        if (items.length) {
          setCategories(items.map((c) => ({ id: publicCategory(c.id) === c.id ? c.id : publicCategory(c.id) || c.id, label: c.label })));
          setActiveTab((prev) => (prev === "all" || items.some((c) => c.id === prev || publicCategory(c.id) === prev) ? prev : "all"));
        }
      })
      .catch(() => {});
    axios.get(`${config.API}/services`, { params: { type: "vaccination" } })
      .then(({ data }) => {
        const items = (data.items || []).filter((s) => s.active !== false);
        if (items.length > 0) {
          setCatalogue(items.map((s) => ({
            id: s.id,
            name: s.name,
            category: publicCategory(s.category),
            ageGroup: s.age_group || s.ageGroup || "",
            brands: s.brands || [],
            pricing: s.pricing || null,
            note: s.description || "",
            type: "vaccination",
            payable_price: s.pricing?.total ?? s.indicative_price ?? s.pricing?.subtotal ?? null,
            indicative_price: s.pricing?.total ?? s.indicative_price ?? s.pricing?.subtotal ?? null,
            price: s.pricing?.total ?? s.indicative_price ?? s.pricing?.subtotal ?? null,
          })));
        } else {
          setCatalogue([]);
        }
      })
      .catch(() => setCatalogue(CATALOGUE.map((s) => ({ ...s, category: publicCategory(s.category) }))))
      .finally(() => setCatalogueReady(true));
  }, []);

  const categoryLabelMap = useMemo(() => {
    const m = {};
    categories.forEach(c => { m[c.id] = c.label; });
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalogue.filter(item => {
      if (!q) {
        if (activeTab && activeTab !== "all" && item.category !== activeTab) return false;
        return true;
      }
      const haystack = [
        item.name,
        item.category,
        item.ageGroup || '',
        ...(item.brands || []),
        categoryLabelMap[item.category] || '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [activeTab, query, categoryLabelMap, catalogue]);

  const [selectedOpen, setSelectedOpen] = useState(false);
  const selectedItems = readBookingCart();
  const appointmentCount = cartAppointmentCount();
  const changeQty = (item, qty) => {
    setCartQty(item, qty);
    setCartTick((n) => n + 1);
  };

  return (
    <div data-testid="vaccination-catalogue-page" className="bg-white min-h-screen">
      <Navbar />

      <main className="pt-24 pb-16">
        <section className="bg-gradient-to-b from-[#fbfaf7] to-white pt-8 pb-8 md:pt-10 md:pb-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-left mb-6">
              <Link to="/" data-testid="catalogue-breadcrumb"
                className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.2em] text-[#673c96]/70 hover:text-[#f37721] transition-colors"
                style={{ fontFamily: 'Montserrat, sans-serif' }}>
                ← Back to Home
              </Link>
            </div>
            <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f37721]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Catalogue
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#311b52] mt-2 mb-3"
              style={{ fontFamily: 'Poppins, sans-serif' }}>
              Vaccination Catalogue
            </h1>
            <p className="text-base text-[#3e195e]/75 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Add one or more vaccines and quantities here. Next you will choose a patient, address and slot for each vaccine.
            </p>

            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto">
              {TRUST.map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.label}
                    data-testid={`catalogue-trust-${t.label.split(' ')[0].toLowerCase()}`}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-xl border border-[#673c96]/10 whitespace-nowrap"
                  >
                    <Icon className="w-4 h-4 text-[#f37721] shrink-0" strokeWidth={1.75} />
                    <span className="text-[12px] text-[#311b52] font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {t.label}
                    </span>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </section>

        <section className="bg-white border-y border-[#673c96]/8 py-4 sticky top-[88px] z-20 backdrop-blur-md bg-white/90">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4">
              <div className="relative max-w-2xl mx-auto w-full">
                <Search className="w-4 h-4 text-[#673c96]/60 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="catalogue-search-input"
                  placeholder="Search vaccine, brand, age group or service"
                  className="w-full pl-11 pr-4 py-3 rounded-full bg-[#fbfaf7] border border-[#673c96]/10 text-sm text-[#311b52] placeholder:text-[#673c96]/50 focus:outline-none focus:border-[#f37721] transition-colors"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                />
              </div>

              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <div className="flex gap-2 justify-start sm:justify-center min-w-max sm:min-w-0">
                  {[{ id: "all", label: "All vaccinations" }, ...categories].map((cat) => {
                    const active = activeTab === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setActiveTab(cat.id)}
                        data-testid={`catalogue-tab-${cat.id}`}
                        className={`text-xs font-semibold rounded-full px-4 py-2 whitespace-nowrap transition-all ${
                          active
                            ? 'bg-[#311b52] text-white'
                            : 'bg-[#fbfaf7] text-[#311b52] hover:bg-[#f37721]/10 hover:text-[#f37721]'
                        }`}
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalogue-grid">
                {filtered.map((item) => (
                  <CatalogueCard
                    key={item.id}
                    item={item}
                    categoryLabel={categoryLabelMap[item.category]}
                    qtyFor={cartQty}
                    onQty={changeQty}
                  />
                ))}
              </div>
            ) : (
              <div data-testid="catalogue-empty-state" className="text-center py-12 max-w-md mx-auto">
                <p className="text-sm text-[#3e195e]/80 mb-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {!catalogueReady ? "Loading vaccines…" : "No matching vaccine found. Try another search or category."}
                </p>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-[11px] text-[#673c96]/70 italic" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                *Vaccination prices are subject to change depending on the changes in MRP or market availability.
              </p>
              <p className="text-[11px] text-[#673c96]/60 italic mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Brand availability and final payable amount are confirmed before scheduling.
              </p>
            </div>
          </div>
        </section>
      </main>

      {appointmentCount > 0 && (
        <div className="fixed left-0 right-0 bottom-20 md:bottom-24 z-40 px-4" data-testid="catalogue-continue-bar">
          <div className="max-w-xl mx-auto bg-[#311b52] text-white rounded-2xl shadow-xl overflow-hidden">
            <button type="button" onClick={() => setSelectedOpen((v) => !v)} className="w-full px-4 py-3 flex items-center justify-between gap-3" data-testid="catalogue-selected-toggle">
              <span className="text-sm font-semibold" style={{ fontFamily: 'Montserrat' }}>
                {appointmentCount} vaccine{appointmentCount === 1 ? '' : 's'} selected
              </span>
              <span className="text-xs text-white/70">{selectedOpen ? "Hide" : "Manage"}</span>
            </button>
            {selectedOpen && (
              <div className="px-4 pb-3 space-y-2 border-t border-white/10" data-testid="catalogue-selected-list">
                {selectedItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate">{item.name}{item.qty > 1 ? ` × ${item.qty}` : ""}</span>
                    <button type="button" className="shrink-0 text-xs font-semibold text-[#f37721]" onClick={() => changeQty(item, 0)} data-testid={`catalogue-remove-${item.id}`}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="px-4 pb-3">
              <Link
                to="/booking"
                className="block text-center bg-[#f37721] hover:bg-[#d9651a] rounded-full px-5 py-2 text-sm font-semibold"
              >
                Continue to slots
              </Link>
            </div>
          </div>
        </div>
      )}

      <span className="hidden">{cartTick}</span>
      <Footer />
    </div>
  );
}
